/**
 * Recurrence rule expander (rule-string + on-demand expansion model).
 *
 * Shared between the React client and Cloud Functions. Implemented in plain
 * JS with no external dependencies so it can be `require`d from CommonJS and
 * imported via ES modules.
 *
 * Recurrence shape (all timestamps in ms-since-epoch where used):
 *   {
 *     freq:        "DAILY" | "WEEKLY" | "BIWEEKLY"
 *                  | "MONTHLY_BY_DATE" | "MONTHLY_BY_WEEKDAY",
 *     interval:    number,           // every N units (default 1)
 *     byDay:       ["MO","TU",...],  // for WEEKLY/BIWEEKLY; or single entry
 *                                    // ["TH"] for MONTHLY_BY_WEEKDAY
 *     byWeekOrdinal: number,         // 1..5 (or -1 for "last") for
 *                                    // MONTHLY_BY_WEEKDAY. Optional;
 *                                    // defaults to ordinal of seedStart.
 *     until:       number|null,      // ms cutoff (inclusive)
 *     count:       number|null,      // max # of occurrences
 *     exDates:     number[],         // ms timestamps to skip (matched on
 *                                    // start-of-occurrence)
 *   }
 *
 * `null` recurrence => one-time event (single instance at seedStart).
 *
 * `seedStart` is the timestamp of the FIRST occurrence (matches event.startAt).
 * `durationMs` controls the end of each generated instance.
 */

const DAY_INDEX = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const HARD_OCCURRENCE_CAP = 365; // safety cap, generous enough for daily-yearly

function isExcluded(exDates, ts) {
  if (!Array.isArray(exDates) || exDates.length === 0) return false;
  for (let i = 0; i < exDates.length; i += 1) {
    // Match within 1-minute tolerance (handles rounding differences).
    if (Math.abs(exDates[i] - ts) < 60 * 1000) return true;
  }
  return false;
}

function clampLimits(rule, opts) {
  const lim = Number.isFinite(opts.limit) ? opts.limit : 12;
  const ruleCount = Number.isFinite(rule.count) ? rule.count : Infinity;
  const fromMs = Number.isFinite(opts.fromMs) ? opts.fromMs : -Infinity;
  const untilMs = Number.isFinite(rule.until) ? rule.until : Infinity;
  return {
    softLimit: Math.min(lim, HARD_OCCURRENCE_CAP),
    ruleCount,
    fromMs,
    untilMs,
  };
}

function addMonths(date, n) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  // Clamp day-of-month so Jan-31 + 1mo doesn't roll into March.
  const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, maxDay));
  return d;
}

/**
 * For MONTHLY_BY_WEEKDAY: compute the ordinal occurrence of a weekday in the
 * given year/month (1-based; -1 means "last"). Returns null if not present.
 */
function nthWeekdayOfMonth(year, monthIdx, dayIndex, ordinal) {
  if (ordinal === -1) {
    // Last weekday-of-month: walk back from last day.
    const lastDay = new Date(year, monthIdx + 1, 0);
    const diff = (lastDay.getDay() - dayIndex + 7) % 7;
    return new Date(year, monthIdx, lastDay.getDate() - diff);
  }
  const first = new Date(year, monthIdx, 1);
  const offset = (dayIndex - first.getDay() + 7) % 7;
  const dayOfMonth = 1 + offset + (ordinal - 1) * 7;
  const candidate = new Date(year, monthIdx, dayOfMonth);
  if (candidate.getMonth() !== monthIdx) return null;
  return candidate;
}

/**
 * Compute which ordinal (1..5) a given date is for its weekday-of-month.
 */
function weekdayOrdinalOf(date) {
  return Math.ceil(date.getDate() / 7);
}

/**
 * Expand a recurrence rule into an array of { startMs, endMs } occurrences.
 *
 *   expandRecurrence({
 *     seedStart:    Date | number,
 *     durationMs:   number,         // applied to every occurrence
 *     recurrence:   rule | null,    // null => single occurrence
 *     fromMs:       number,         // skip occurrences ending before this
 *     limit:        number,         // max instances to return
 *   })
 */
function expandRecurrence({
  seedStart,
  durationMs = 0,
  recurrence = null,
  fromMs = -Infinity,
  limit = 12,
}) {
  const seed = seedStart instanceof Date
    ? new Date(seedStart.getTime())
    : new Date(seedStart);
  if (Number.isNaN(seed.getTime())) return [];

  // One-time event: single occurrence.
  if (!recurrence || !recurrence.freq || recurrence.freq === "ONE_TIME") {
    const startMs = seed.getTime();
    const endMs = startMs + (Number.isFinite(durationMs) ? durationMs : 0);
    if (endMs < fromMs) return [];
    return [{ startMs, endMs }];
  }

  const { softLimit, ruleCount, untilMs } = clampLimits(recurrence, { limit, fromMs });
  const interval = Math.max(1, Math.floor(recurrence.interval || 1));
  const byDay = Array.isArray(recurrence.byDay) && recurrence.byDay.length > 0
    ? recurrence.byDay
    : null;
  const exDates = Array.isArray(recurrence.exDates) ? recurrence.exDates : [];

  const out = [];
  let produced = 0;

  const pushIfValid = (date) => {
    if (out.length >= softLimit) return false;
    if (produced >= ruleCount) return false;
    const startMs = date.getTime();
    if (startMs > untilMs) return false;
    const endMs = startMs + (Number.isFinite(durationMs) ? durationMs : 0);
    if (isExcluded(exDates, startMs)) {
      produced += 1; // exDates count toward `count` per RFC-5545 spirit
      return true;
    }
    produced += 1;
    if (endMs >= fromMs) out.push({ startMs, endMs });
    return true;
  };

  if (recurrence.freq === "DAILY") {
    let cur = new Date(seed.getTime());
    for (let i = 0; i < HARD_OCCURRENCE_CAP; i += 1) {
      if (!pushIfValid(cur)) break;
      cur = new Date(cur.getTime() + interval * ONE_DAY_MS);
    }
    return out;
  }

  if (recurrence.freq === "WEEKLY" || recurrence.freq === "BIWEEKLY") {
    const weekStride = recurrence.freq === "BIWEEKLY" ? 2 : interval;
    // Days to fire on. If byDay omitted, use seed's weekday.
    const dayKeys = byDay || [Object.keys(DAY_INDEX).find((k) => DAY_INDEX[k] === seed.getDay())];
    const sortedDayIdx = dayKeys
      .map((k) => DAY_INDEX[k])
      .filter((idx) => Number.isInteger(idx))
      .sort((a, b) => a - b);
    if (sortedDayIdx.length === 0) return out;

    const hours = seed.getHours();
    const minutes = seed.getMinutes();

    // Walk by week, emitting an occurrence on each selected weekday.
    // Start from the Sunday of the seed week.
    const seedSunday = new Date(seed.getTime());
    seedSunday.setDate(seed.getDate() - seed.getDay());
    seedSunday.setHours(0, 0, 0, 0);

    let weekStart = new Date(seedSunday.getTime());
    let safety = 0;
    while (safety < HARD_OCCURRENCE_CAP * 2) {
      safety += 1;
      for (let i = 0; i < sortedDayIdx.length; i += 1) {
        const day = sortedDayIdx[i];
        const occ = new Date(weekStart.getTime());
        occ.setDate(weekStart.getDate() + day);
        occ.setHours(hours, minutes, 0, 0);
        if (occ.getTime() < seed.getTime()) continue; // before series start
        if (!pushIfValid(occ)) return out;
      }
      weekStart = new Date(weekStart.getTime() + weekStride * 7 * ONE_DAY_MS);
      if (out.length >= softLimit || produced >= ruleCount) break;
    }
    return out;
  }

  if (recurrence.freq === "MONTHLY_BY_DATE") {
    let cur = new Date(seed.getTime());
    for (let i = 0; i < HARD_OCCURRENCE_CAP; i += 1) {
      if (!pushIfValid(cur)) break;
      cur = addMonths(cur, interval);
    }
    return out;
  }

  if (recurrence.freq === "MONTHLY_BY_WEEKDAY") {
    const dayKey = (byDay && byDay[0]) || Object.keys(DAY_INDEX).find((k) => DAY_INDEX[k] === seed.getDay());
    const dayIndex = DAY_INDEX[dayKey];
    if (!Number.isInteger(dayIndex)) return out;
    const ordinal = Number.isFinite(recurrence.byWeekOrdinal)
      ? recurrence.byWeekOrdinal
      : weekdayOrdinalOf(seed);
    const hours = seed.getHours();
    const minutes = seed.getMinutes();
    let cursor = new Date(seed.getFullYear(), seed.getMonth(), 1);
    for (let i = 0; i < HARD_OCCURRENCE_CAP; i += 1) {
      const occDate = nthWeekdayOfMonth(cursor.getFullYear(), cursor.getMonth(), dayIndex, ordinal);
      if (occDate) {
        occDate.setHours(hours, minutes, 0, 0);
        if (occDate.getTime() >= seed.getTime()) {
          if (!pushIfValid(occDate)) break;
        }
      }
      cursor = addMonths(cursor, interval);
    }
    return out;
  }

  return out;
}

/**
 * Convenience: return ONLY the next active occurrence (or null).
 * "Active" means now is inside [start - graceBefore, end + graceAfter].
 * If no current occurrence, returns the next upcoming.
 */
function nextOccurrence({ seedStart, durationMs, recurrence, nowMs = Date.now(), grace = { before: 0, after: 0 } }) {
  const all = expandRecurrence({
    seedStart,
    durationMs,
    recurrence,
    fromMs: nowMs - (Number.isFinite(durationMs) ? durationMs : 0) - (grace.after || 0) * 60 * 1000,
    limit: 6,
  });
  for (let i = 0; i < all.length; i += 1) {
    const occ = all[i];
    const winStart = occ.startMs - (grace.before || 0) * 60 * 1000;
    const winEnd = occ.endMs + (grace.after || 0) * 60 * 1000;
    if (nowMs >= winStart && nowMs <= winEnd) {
      return { ...occ, isActive: true };
    }
    if (occ.startMs >= nowMs) {
      return { ...occ, isActive: false };
    }
  }
  return null;
}

/**
 * Human-friendly summary of a recurrence rule.
 * Returns e.g. "Every Mon, Wed, Fri from 4:00 PM to 6:00 PM through Dec 31, 2026"
 * or null for one-time events.
 */
function describeRecurrence(rule, opts = {}) {
  if (!rule || !rule.freq || rule.freq === "ONE_TIME") return null;
  const labels = {
    MO: "Mon", TU: "Tue", WE: "Wed", TH: "Thu", FR: "Fri", SA: "Sat", SU: "Sun",
  };
  const dayList = (rule.byDay || []).map((k) => labels[k]).filter(Boolean).join(", ");
  let freq;
  switch (rule.freq) {
    case "DAILY":
      freq = rule.interval > 1 ? `Every ${rule.interval} days` : "Daily";
      break;
    case "WEEKLY":
      freq = dayList ? `Every ${dayList}` : "Weekly";
      break;
    case "BIWEEKLY":
      freq = dayList ? `Every other ${dayList}` : "Every 2 weeks";
      break;
    case "MONTHLY_BY_DATE":
      freq = "Monthly";
      break;
    case "MONTHLY_BY_WEEKDAY": {
      const ordinals = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th", "-1": "last" };
      const ordKey = Number.isFinite(rule.byWeekOrdinal) ? String(rule.byWeekOrdinal) : "1";
      freq = `Monthly on the ${ordinals[ordKey] || "1st"} ${dayList || "Mon"}`;
      break;
    }
    default:
      freq = "Recurring";
  }
  const parts = [freq];
  if (opts.timeWindow) parts.push(opts.timeWindow);
  if (rule.until) {
    const d = new Date(rule.until);
    parts.push(
      `through ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
    );
  } else if (Number.isFinite(rule.count)) {
    parts.push(`(${rule.count} occurrences)`);
  }
  return parts.join(" ");
}

const recurrenceLib = {
  expandRecurrence,
  nextOccurrence,
  describeRecurrence,
  DAY_INDEX,
};

export { expandRecurrence, nextOccurrence, describeRecurrence, DAY_INDEX };
export default recurrenceLib;
